import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { permissionsAPI } from '../services/api';
import { toast } from 'react-toastify';

export const PermissionsContext = createContext();

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({
    location: { granted: false, granted_at: null, revoked_at: null },
    camera: { granted: false, granted_at: null, revoked_at: null },
    microphone: { granted: false, granted_at: null, revoked_at: null },
    notifications: { granted: false, granted_at: null, revoked_at: null },
  });
  const [loading, setLoading] = useState(true);

  // Load user permissions from backend
  const loadPermissions = useCallback(async () => {
    try {
      const response = await permissionsAPI.getUserPermissions();
      const data = response.data || response;

      if (data && data.success && data.permissions) {
        // Merge with default permissions to ensure all types exist
        setPermissions({
          location: data.permissions.location || { granted: false, granted_at: null, revoked_at: null },
          camera: data.permissions.camera || { granted: false, granted_at: null, revoked_at: null },
          microphone: data.permissions.microphone || { granted: false, granted_at: null, revoked_at: null },
          notifications: data.permissions.notifications || { granted: false, granted_at: null, revoked_at: null },
        });
      } else {
        console.warn('Using default permissions (API response invalid or offline mode)');
        setPermissions({
          location: { granted: false, granted_at: null, revoked_at: null },
          camera: { granted: false, granted_at: null, revoked_at: null },
          microphone: { granted: false, granted_at: null, revoked_at: null },
          notifications: { granted: false, granted_at: null, revoked_at: null },
        });
      }
    } catch (error) {
      console.warn('Using default permissions (API unavailable):', error.message);
      setPermissions({
        location: { granted: false, granted_at: null, revoked_at: null },
        camera: { granted: false, granted_at: null, revoked_at: null },
        microphone: { granted: false, granted_at: null, revoked_at: null },
        notifications: { granted: false, granted_at: null, revoked_at: null },
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Update permission in both backend and local state
  const updatePermission = useCallback(async (permissionType, granted) => {
    try {
      const response = await permissionsAPI.updatePermission(permissionType, granted);
      const data = response.data || response;

      if (data && data.success) {
        setPermissions(prev => ({
          ...prev,
          [permissionType]: {
            ...prev[permissionType],
            granted,
            granted_at: granted ? new Date().toISOString() : prev[permissionType].granted_at,
            revoked_at: !granted ? new Date().toISOString() : null,
          }
        }));
        return true;
      }
    } catch (error) {
      console.error('Failed to update permission:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(`Failed to update permission: ${error.response?.data?.message || error.message}`);
    }
    return false;
  }, []);

  // Request browser permission and update backend
  const requestBrowserPermission = useCallback(async (permissionType) => {
    try {
      let browserGranted = false;

      switch (permissionType) {
        case 'location':
          if ('geolocation' in navigator) {
            try {
              // Check if we're in HTTPS or localhost for development
              const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

              if (!isSecure) {
                // In development (HTTP), mock location permission as granted
                console.log('Development mode: Mocking location permission as granted');
                browserGranted = true;
              } else {
                // In production (HTTPS), request actual permission
                await new Promise((resolve, reject) => {
                  navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000
                  });
                });
                browserGranted = true;
              }
            } catch (error) {
              console.log('Location permission denied or error:', error);
              // In development, still allow if it's a timeout or other error
              if (window.location.hostname === 'localhost' && error.code === 3) { // TIMEOUT
                console.log('Development timeout: Allowing location permission anyway');
                browserGranted = true;
              }
            }
          }
          break;

        case 'camera':
          if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
              const stream = await navigator.mediaDevices.getUserMedia({ video: true });
              stream.getTracks().forEach(track => track.stop());
              browserGranted = true;
            } catch (error) {
              console.log('Camera permission denied or error:', error);
            }
          }
          break;

        case 'microphone':
          if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
              const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
              stream.getTracks().forEach(track => track.stop());
              browserGranted = true;
            } catch (error) {
              console.log('Microphone permission denied or error:', error);
            }
          }
          break;

        case 'notifications':
          if ('Notification' in window) {
            const permission = await Notification.requestPermission();
            browserGranted = permission === 'granted';
          }
          break;

        default:
          return false;
      }

      // Update backend with the result
      if (browserGranted) {
        await updatePermission(permissionType, true);
        toast.success(`${permissionType} permission granted!`);
        return true;
      } else {
        toast.info(`${permissionType} permission denied`);
        return false;
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error(`Failed to request ${permissionType} permission`);
      return false;
    }
  }, [updatePermission]);

  // Check current browser permission status
  const checkBrowserPermission = useCallback(async (permissionType) => {
    try {
      switch (permissionType) {
        case 'location':
          // In development, always return true for location support
          return window.location.hostname === 'localhost' || 'geolocation' in navigator;

        case 'camera':
        case 'microphone':
          if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
              const permission = await navigator.permissions.query({
                name: permissionType === 'camera' ? 'camera' : 'microphone'
              });
              return permission.state === 'granted';
            } catch (error) {
              return false;
            }
          }
          return false;

        case 'notifications':
          if ('Notification' in window) {
            return Notification.permission === 'granted';
          }
          return false;

        default:
          return false;
      }
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  }, []);

  // Initialize permissions on mount
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const value = {
    permissions,
    loading,
    updatePermission,
    requestBrowserPermission,
    checkBrowserPermission,
    loadPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
